// Modal de criar/editar lista de arquivos (aba Arquivos de Campanhas), no
// padrão Aeros Dark. Cada arquivo é uma linha-card com tile de ícone por
// tipo, no molde do "Bloco de conteúdo" do Flowbuilder.
//
// Contrato preservado do upload: `POST /files/uploadList/:id` recebe os
// arquivos pareados POR ÍNDICE com as `options` devolvidas pelo servidor
// (que trazem os ids) — por isso a ordem de `options` não pode ser alterada
// entre o salvar e o upload.
import React, { useState, useEffect, useContext } from "react";

import { toast } from "react-toastify";
import { makeStyles, alpha } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";

import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import ImageIcon from "@material-ui/icons/Image";
import MicIcon from "@material-ui/icons/Mic";
import VideocamIcon from "@material-ui/icons/Videocam";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import AerosDialog from "../AerosDialog";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    optionRow: {
      display: "flex",
      gap: 10,
      alignItems: "flex-start",
      backgroundColor: isLight ? theme.palette.aeros.surface2 : "#12151F",
      border: `1px solid ${
        isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"
      }`,
      borderRadius: 11,
      padding: "10px 12px",
    },
    tile: {
      width: 30,
      height: 30,
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      marginTop: 2,
      backgroundColor: alpha(AEROS.cyan, 0.12),
      color: stateText(theme, AEROS.cyan, "info"),
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    optionBody: { flex: 1, minWidth: 0 },
    fileName: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      marginTop: 6,
      display: "flex",
      alignItems: "center",
      gap: 6,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    fileNameEmpty: { color: theme.palette.aeros.textTertiary },
    rowActions: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
      flex: "none",
    },
    iconButton: {
      padding: 5,
      color: theme.palette.text.secondary,
      "& .MuiSvgIcon-root": { fontSize: 17 },
      "&:hover": { color: stateText(theme, AEROS.cyan, "info") },
    },
    iconButtonDanger: {
      "&:hover": { color: stateText(theme, AEROS.error, "error") },
    },
    addButton: {
      borderRadius: 10,
      padding: "9px 12px",
      fontSize: 12.5,
      fontWeight: 600,
      textTransform: "none",
      width: "100%",
      border: `1px dashed ${
        isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.15)"
      }`,
      color: theme.palette.text.secondary,
      "&:hover": {
        borderColor: "rgba(160,68,255,0.5)",
        color: theme.palette.text.primary,
        backgroundColor: "transparent",
      },
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    optionsList: {
      display: "flex",
      flexDirection: "column",
      gap: 8,
    },
    empty: {
      border: `1px dashed ${
        isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.15)"
      }`,
      borderRadius: 11,
      padding: 14,
      textAlign: "center",
      fontSize: 12,
      color: theme.palette.aeros.textTertiary,
    },
  };
});

const EMPTY_OPTION = { name: "", path: "", mediaType: "" };

// Ícone do tile pelo tipo de mídia (ou pela extensão, quando só há o path).
const iconFor = (option, pickedFile) => {
  const type = pickedFile?.type || option.mediaType || "";
  const source = pickedFile?.name || option.path || "";
  const ext = source.split(".").pop().toLowerCase();
  if (type.startsWith("image") || ["png", "jpg", "jpeg", "gif", "webp"].includes(ext))
    return <ImageIcon />;
  if (type.startsWith("audio") || ["mp3", "ogg", "wav", "m4a"].includes(ext))
    return <MicIcon />;
  if (type.startsWith("video") || ["mp4", "mov", "avi", "mkv"].includes(ext))
    return <VideocamIcon />;
  return <InsertDriveFileIcon />;
};

const FilesModal = ({ open, onClose, fileListId, reload }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const { user } = useContext(AuthContext);

  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [options, setOptions] = useState([{ ...EMPTY_OPTION }]);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTouched(false);

    if (!fileListId) {
      setName("");
      setMessage("");
      setOptions([{ ...EMPTY_OPTION }]);
      return;
    }

    (async () => {
      try {
        const { data } = await api.get(`/files/${fileListId}`);
        setName(data?.name || "");
        setMessage(data?.message || "");
        setOptions(
          data?.options?.length
            ? data.options.map((o) => ({ ...o }))
            : [{ ...EMPTY_OPTION }]
        );
      } catch (err) {
        toastError(err);
      }
    })();
  }, [fileListId, open]);

  const trimmedName = name.trim();
  const trimmedMessage = message.trim();
  const errors = {
    name:
      trimmedName.length < 3 ? i18n.t("fileModal.form.nameRequired") : "",
    message: !trimmedMessage ? i18n.t("fileModal.form.messageRequired") : "",
  };
  const hasError = Boolean(errors.name || errors.message);
  const shown = (key) => (touched ? errors[key] : "");

  const handleClose = () => {
    setName("");
    setMessage("");
    setOptions([{ ...EMPTY_OPTION }]);
    setTouched(false);
    onClose();
  };

  const updateOption = (index, patch) =>
    setOptions((prev) =>
      prev.map((option, i) => (i === index ? { ...option, ...patch } : option))
    );

  const removeOption = (index) =>
    setOptions((prev) => prev.filter((_, i) => i !== index));

  const handleSave = async () => {
    setTouched(true);
    if (hasError) return;
    setSaving(true);

    // Envia os arquivos escolhidos, pareados por índice com as options que o
    // servidor devolveu (é delas que vem o id de cada arquivo).
    const uploadFiles = async (serverOptions, localOptions, id) => {
      const formData = new FormData();
      formData.append("fileId", id);
      formData.append("typeArch", "fileList");
      localOptions.forEach((option, index) => {
        if (option.file) {
          formData.append("files", option.file);
          formData.append("mediaType", option.file.type);
          formData.append("name", serverOptions[index].name);
          formData.append("id", serverOptions[index].id);
        }
      });

      try {
        const { data } = await api.post(`/files/uploadList/${id}`, formData);
        return data;
      } catch (err) {
        toastError(err);
      }
      return null;
    };

    const fileData = {
      name: trimmedName,
      message: trimmedMessage,
      options,
      userId: user.id,
    };

    try {
      if (fileListId) {
        const { data } = await api.put(`/files/${fileListId}`, fileData);
        if (data.options.length > 0) {
          await uploadFiles(data.options, options, fileListId);
        }
      } else {
        const { data } = await api.post("/files", fileData);
        if (data.options.length > 0) {
          await uploadFiles(data.options, options, data.id);
        }
      }
      toast.success(i18n.t("fileModal.success"));
      if (typeof reload === "function") {
        reload();
      }
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AerosDialog
      open={open}
      onClose={handleClose}
      width={680}
      title={i18n.t(fileListId ? "fileModal.title.edit" : "fileModal.title.add")}
      subtitle={i18n.t("fileModal.subtitle")}
      onSubmit={handleSave}
      loading={saving}
      submitLabel={i18n.t(
        fileListId ? "fileModal.buttons.okEdit" : "fileModal.buttons.okAdd"
      )}
      cancelLabel={i18n.t("fileModal.buttons.cancel")}
    >
      <div>
        <label
          className={`${m.fieldLabel} ${
            !shown("name") && trimmedName ? m.fieldLabelFocus : ""
          }`}
          htmlFor="file-list-name"
        >
          {i18n.t("fileModal.form.name")}
        </label>
        <TextField
          id="file-list-name"
          variant="outlined"
          className={m.input}
          value={name}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          onBlur={() => setTouched(true)}
          error={Boolean(shown("name"))}
          helperText={shown("name")}
        />
      </div>

      <div>
        <label className={m.fieldLabel} htmlFor="file-list-message">
          {i18n.t("fileModal.form.message")}
        </label>
        <TextField
          id="file-list-message"
          variant="outlined"
          className={m.input}
          value={message}
          multiline
          minRows={4}
          onChange={(e) => setMessage(e.target.value)}
          onBlur={() => setTouched(true)}
          error={Boolean(shown("message"))}
          helperText={shown("message")}
        />
      </div>

      <div>
        <label className={m.fieldLabel}>
          {i18n.t("fileModal.form.fileOptions")}
        </label>
        <div className={classes.optionsList}>
          {options.length === 0 && (
            <div className={classes.empty}>
              {i18n.t("fileModal.form.emptyFiles")}
            </div>
          )}

          {options.map((option, index) => {
            const picked = option.file;
            const currentName = option.path
              ? option.path.split("/").pop()
              : picked?.name;
            return (
              <div className={classes.optionRow} key={`option-${index}`}>
                <div className={classes.tile}>{iconFor(option, picked)}</div>
                <div className={classes.optionBody}>
                  <TextField
                    variant="outlined"
                    className={m.input}
                    value={option.name || ""}
                    multiline
                    minRows={2}
                    placeholder={i18n.t("fileModal.form.extraName")}
                    inputProps={{
                      "aria-label": i18n.t("fileModal.form.extraName"),
                    }}
                    onChange={(e) => updateOption(index, { name: e.target.value })}
                  />
                  <div
                    className={`${classes.fileName} ${
                      currentName ? "" : classes.fileNameEmpty
                    }`}
                  >
                    <AttachFileIcon style={{ fontSize: 14 }} />
                    {currentName || i18n.t("fileModal.form.pickFile")}
                  </div>
                </div>
                <div className={classes.rowActions}>
                  <input
                    type="file"
                    style={{ display: "none" }}
                    id={`file-upload-${index}`}
                    onChange={(e) =>
                      updateOption(index, { file: e.target.files[0] })
                    }
                  />
                  <label htmlFor={`file-upload-${index}`}>
                    <Tooltip title={i18n.t("fileModal.form.pickFile")}>
                      <IconButton component="span" className={classes.iconButton}>
                        <AttachFileIcon />
                      </IconButton>
                    </Tooltip>
                  </label>
                  <Tooltip title={i18n.t("fileModal.form.removeFile")}>
                    <IconButton
                      className={`${classes.iconButton} ${classes.iconButtonDanger}`}
                      aria-label={i18n.t("fileModal.form.removeFile")}
                      onClick={() => removeOption(index)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            );
          })}

          <Button
            className={classes.addButton}
            startIcon={<AddIcon />}
            onClick={() => setOptions((prev) => [...prev, { ...EMPTY_OPTION }])}
          >
            {i18n.t("fileModal.buttons.fileOptions")}
          </Button>
        </div>
      </div>
    </AerosDialog>
  );
};

export default FilesModal;
